(ns admin.containers.user-management
  (:require
   [applied-science.js-interop :as j]
   [re-frame.core :as re-frame]
   [reagent.core :as reagent]
   [kyber.feature-flags :refer [feature-enabled?]]
   [kyber.components.base.icons :as icon]
   [kyber.components.base.button :as button]
   [kyber.components.base.text :as text]
   [kyber.components.composite.toggle :as toggle]
   [kyber.components.composite.checkbox :as checkbox]
   [kyber.components.composite.pagination-controls :as pagination-controls]
   [kyber.styles.core :refer [gs styles->classes]]
   [kyber.utils :as ku]
   [kyber.components.composite.loading :as loading]))

(def curriculum-column-min-width 300)
(def column-cell-min-width 150)

(def ^{:private true} search-users
  (ku/debounce #(re-frame/dispatch [:users/search-users %])
               300))

(def ^{:private true} search-users-instant
  #(re-frame/dispatch [:users/search-users %]))

(defn- add-super-user
  [{:keys [given-name family-name current-value user-uuid]}]
  (re-frame/dispatch
   [:notifier/create-alert
    {:title "Make a super user?"
     :message (str "Are you sure you want to make "
                   given-name " " family-name
                   " a super user? They will have full access "
                   "to all curricula and activities.")
     :confirm-action #(re-frame/dispatch [:users/toggle-top-level-permission
                                          {:policy-name :super-user
                                           :enabled true
                                           :user-uuid user-uuid}])
     :deny-action (fn []
                    (reset! current-value false))}]))

(defn- remove-super-user
  [{:keys [given-name family-name current-value user-uuid]}]
  (re-frame/dispatch
   [:notifier/create-alert
    {:title "Remove super user?"
     :message (str "Are you sure you want to remove super user privileges from "
                   given-name " " family-name
                   "? They will no longer have access to the admin portal.")
     :confirm-action #(re-frame/dispatch [:users/toggle-top-level-permission
                                          {:policy-name :super-user
                                           :enabled false
                                           :user-uuid user-uuid}])
     :deny-action (fn []
                    (reset! current-value true))}]))

(defn- add-test-user
  [{:keys [given-name family-name current-value user-uuid]}]
  (re-frame/dispatch
   [:notifier/create-alert
    {:title "Make a test user?"
     :message (str "Are you sure you want to make "
                   given-name " " family-name
                   " a test user? They will be able to view activities that "
                   "are in draft and no activity data will be tracked on them.")
     :confirm-action #(re-frame/dispatch [:users/toggle-top-level-permission
                                          {:policy-name :test-user
                                           :enabled true
                                           :user-uuid user-uuid}])
     :deny-action (fn []
                    (reset! current-value false))}]))

(defn- remove-test-user
  [{:keys [given-name family-name current-value user-uuid]}]
  (re-frame/dispatch
   [:notifier/create-alert
    {:title "Remove test user?"
     :message (str "Are you sure you want to remove "
                   given-name " " family-name
                   "as a test user? Their data will then be tracked when "
                   "interacting with activities.")
     :confirm-action #(re-frame/dispatch [:users/toggle-top-level-permission
                                          {:policy-name :test-user
                                           :enabled false
                                           :user-uuid user-uuid}])
     :deny-action (fn []
                    (reset! current-value true))}]))

(defn- add-alpha-user
  [{:keys [given-name family-name current-value user-uuid]}]
  (re-frame/dispatch
   [:notifier/create-alert
    {:title "Make an alpha user?"
     :message (str "Are you sure you want to make "
                   given-name " " family-name
                   " an alpha user? They will be able to view alpha previews "
                   "of all new features.")
     :confirm-action #(re-frame/dispatch [:users/toggle-top-level-permission
                                          {:policy-name :alpha-user
                                           :enabled true
                                           :user-uuid user-uuid}])
     :deny-action (fn []
                    (reset! current-value false))}]))

(defn- remove-alpha-user
  [{:keys [given-name family-name current-value user-uuid]}]
  (re-frame/dispatch
   [:notifier/create-alert
    {:title "Remove alpha user?"
     :message (str "Are you sure you want to remove "
                   given-name " " family-name
                   "as an alpha user?")
     :confirm-action #(re-frame/dispatch [:users/toggle-top-level-permission
                                          {:policy-name :alpha-user
                                           :enabled false
                                           :user-uuid user-uuid}])
     :deny-action (fn []
                    (reset! current-value true))}]))

(defn- add-content-provider-user
  [{:keys [given-name family-name current-value user-uuid]}]
  (re-frame/dispatch
   [:notifier/create-alert
    {:title "Make a content manager?"
     :message (str "Are you sure you want to make "
                   given-name " " family-name
                   " a content manager? They will be able to make changes "
                   "that will impact activity build out.")
     :confirm-action #(re-frame/dispatch [:users/toggle-top-level-permission
                                          {:policy-name :content-provider-user
                                           :enabled true
                                           :user-uuid user-uuid}])
     :deny-action (fn []
                    (reset! current-value false))}]))

(defn- remove-content-provider-user
  [{:keys [given-name family-name current-value user-uuid]}]
  (re-frame/dispatch
   [:notifier/create-alert
    {:title "Remove content manager?"
     :message (str "Are you sure you want to remove "
                   given-name " " family-name
                   "as a content manager?")
     :confirm-action #(re-frame/dispatch [:users/toggle-top-level-permission
                                          {:policy-name :content-provider-user
                                           :enabled false
                                           :user-uuid user-uuid}])
     :deny-action (fn []
                    (reset! current-value true))}]))

(def classes
  (styles->classes
   {:page-container
    {}

    :banner-container
    {:padding [(gs [:spacing :p20])
               (gs [:spacing :p20])]
     :background (gs [:overlay :partner])}

    :title
    {:padding [(gs [:spacing :p8])
               (gs [:spacing :p0])]
     :color (gs [:colors :true-white])}

    :search-area-container
    {:display :flex
     :justify-content :space-between
     :align-items :center
     :padding (gs [:spacing :p20])}

    :body-container
    {:padding (gs [:spacing :p20])
     :background (gs [:colors :true-white])}

    :no-users-message-container
    {:display :flex
     :justify-content :center
     :align-items :center
     :min-height "50vh"}

    :results-container
    {:max-width "1200px"
     :margin :auto}

    :user-row-container
    {"& > *"
     {:padding (gs [:spacing :p20])
      :box-shadow (gs [:shadows :shadow-6])
      :margin-bottom (gs [:spacing :p20])

      "&:first-child"
      {:border-top (str "1px solid " (gs [:colors :gray-scale-200]))}}}

    :user-card-top-container
    {:display :flex
     :justify-content :space-between
     :align-items :flex-end}

    :user-card-metadata-container
    {:display :flex}

    :super-user-toggle-container
    {:padding [(gs [:spacing :p0])
               (gs [:spacing :p8])]
     :background-color "rgba(255,0,0,0.1)"
     :border (str "1px solid " (gs [:colors :error-500]))
     :border-radius (gs [:radius :r3])}

    :test-user-toggle-container
    {:padding [(gs [:spacing :p0])
               (gs [:spacing :p8])]
     :border (str "1px solid " (gs [:colors :gray-scale-300]))
     :border-radius (gs [:radius :r3])}

    :top-level-user-ctas
    {:display :flex
     :align-items :center

     "& > *"
     {:margin-right (gs [:spacing :p20])

      "&:last-child"
      {:margin-right (gs [:spacing :p0])}}}

    :permissions-container
    {:display :flex
     :justify-content :space-between}

    :role-container
    {:display :flex

     "& > *"
     {:margin-right (gs [:spacing :p20])}}

    :user-search-container
    {:width "100%"
     :max-width "700px"}

    :loading-container
    {:position :absolute
     :right (gs [:spacing :p8])
     :bottom 0}

    :filters-container
    {:display :flex

     "& > *"
     {:margin-right (gs [:spacing :p20])}}

    :table-container
    {:overflow :hidden
     :transition "transform 0.3s ease-in-out"
     :height :auto
     :transform "scaleY(1)"
     :transform-origin :top
     :width "100%"
     :border-top (str "1px solid " (gs [:colors :gray-scale-200]))
     :border-bottom (str "1px solid " (gs [:colors :gray-scale-200]))
     :margin-top (gs [:spacing :p20])

     "& th"
     {:font-weight (gs [:font-weight :title-10])}}

    :table-container-collapsed
    {:border-top (str "1px solid " (gs [:colors :gray-scale-200]))
     :transform "scaleY(0)"
     :margin-top (gs [:spacing :p0])}

    :table-header-row
    {:display :flex
     :border-bottom (str "1px solid " (gs [:colors :gray-scale-200]))

     "& > *"
     {:padding-top (gs [:spacing :p20])
      :padding-bottom (gs [:spacing :p20])}}

    :table-body-row
    {:display :flex
     :padding [(gs [:spacing :p0])
               (gs [:spacing :p4])]

     "&:nth-child(2n+2)"
     {:background-color (gs [:colors :gray-scale-100])}

     "& > *"
     {:padding-top (gs [:spacing :p8])
      :padding-bottom (gs [:spacing :p8])
      :border-bottom (str "1px solid " (gs [:colors :gray-scale-100]))}

     "&:last-child"
     {"& > *"
      {:border-bottom :none}}}

    :table-body-row-disabled
    {:background-color (gs [:colors :gray-scale-50])}

    :column-container
    {:min-width column-cell-min-width}

    :async-container
    {:width (gs [:spacing :p40])}

    :checkbox-cell
    {:width "100%"
     :display :flex
     :justify-content :center}}))

(defn- render-user-list-pagination
  [{:keys [pagination search-value search-filters]}]
  [:div {:style {:margin (gs [:spacing :p20])}}
   [pagination-controls/render {:on-change #(search-users-instant
                                             {:q @search-value
                                              :filter @search-filters
                                              :page %})}
    pagination]])

(defn- render-policy-checkbox
  [{:keys [curriculum-uuid user-uuid policy is-updating-row roles super-user]}]
  [:td {:class (:column-container classes)}
   [:div {:class (:checkbox-cell classes)}
    [checkbox/render-v2
     {:default-value (or super-user
                         (contains? roles (name policy)))
      :disabled (or @is-updating-row
                    (true? super-user))
      :on-change #(re-frame/dispatch
                   [:users/toggle-curriculum-level-permission
                    {:curriculum-uuid curriculum-uuid
                     :user-uuid user-uuid
                     :policy policy
                     :enabled %}])}]]])

(defn- render-curriculum-permissions
  [{:keys [curriculum user-uuid]}]
  (let [is-updating-row (re-frame/subscribe
                         [:request/is-dispatching
                          (keyword "users"
                                   (str "toggle-curriculum-level-permission-"
                                        (:uuid curriculum)
                                        "-" user-uuid))])]
    (fn [{:keys [curriculum curriculum-permissions user-uuid super-user]}]
      (let [{curriculum-name :name
             curriculum-uuid :uuid} curriculum
            roles (set (:roles curriculum-permissions))

            checkbox-props {:curriculum-uuid curriculum-uuid
                            :user-uuid user-uuid
                            :is-updating-row is-updating-row
                            :roles roles
                            :super-user super-user}]
        [:tr {:class (cond-> [(:table-body-row classes)]
                       (true? @is-updating-row)
                       (conj (:table-body-row-disabled classes)))
              :key (str "super-user-" super-user)}
         [:td {:style {:width "100%"
                       :min-width curriculum-column-min-width}}
          curriculum-name]
         [render-policy-checkbox
          (assoc checkbox-props :policy :admin)]

         [render-policy-checkbox
          (assoc checkbox-props :policy :content-manager)]

         [render-policy-checkbox
          (assoc checkbox-props :policy :insights-viewer)]
         
         (when (feature-enabled? :learner-table)
           [render-policy-checkbox
            (assoc checkbox-props :policy :project-dashboard-viewer)])

         [:td {:class (:async-container classes)}
          [:div
           (when (true? @is-updating-row)
             [icon/icon {:class "fa-spin"} :loading])]]]))))

(defn- render-user-row
  [_]
  (let [show-permissions-table (reagent/atom false)]
    (fn [{:keys [user can-manage-super-users curricula]}]
      (let [{:keys [family-name
                    given-name
                    email
                    curricula-permissions
                    super-user
                    test-user
                    alpha-user
                    content-provider-user]}
            user]
        [:li
         [:div {:class (:user-card-top-container classes)}
          [:div {:class (:user-metadata-container classes)}
           [:div {:style {:display :flex}}
            [text/title-30 :p
             {:style (cond-> {:margin-top (gs [:spacing :p0])}
                       (nil? family-name)
                       (assoc :color (gs [:colors :gray-scale-400])))}
             (or family-name "No last name")]
            [text/title-30 :p
             {:style {:margin-top (gs [:spacing :p0])
                      :margin-right (gs [:spacing :p8])}}
             ","]
            [text/title-30 :p
             {:style (cond-> {:margin-top (gs [:spacing :p0])}
                       (nil? given-name)
                       (assoc :color (gs [:colors :gray-scale-400])))}
             (or given-name "No first name")]]
           [text/caption-30 :p email]
           [button/button-link
            {:on-click #(swap! show-permissions-table not)
             :data-test "show-hide-permissions"}
            (if (true? @show-permissions-table)
              "Hide Permissions"
              "Show Permissions")]]

          [:div {:class (:top-level-user-ctas classes)}
           (when (true? test-user)
             [button/secondary-button
              {:on-click
               (fn []
                 (re-frame/dispatch
                  [:notifier/create-alert
                   {:title "Remove Test User Data"
                    :message (str "Resetting test user data will reset all of "
                                  "the test users' data as if they we're a "
                                  "brand new user.")
                    :confirm-text "Reset Data"
                    :confirm-action #(re-frame/dispatch
                                      [:users/clear-test-user-data
                                       {:user-uuid (:uuid user)}])}]))
               :data-test "clear-test-user-data"}
              "Clear Test User Data"])

           [:div {:class (:test-user-toggle-container classes)}
            [toggle/render
             {:default-value test-user
              :pass-atom-on-change true
              :on-change (fn [current-value]
                           (let [options {:given-name given-name
                                          :family-name family-name
                                          :current-value current-value
                                          :user-uuid (:uuid user)}]
                             (if (true? @current-value)
                               (add-test-user options)
                               (remove-test-user options))))
              :label "Test User"}]]

           (when (true? @can-manage-super-users)
             [:div {:class (:super-user-toggle-container classes)}
              [toggle/render
               {:default-value alpha-user
                :pass-atom-on-change true
                :on-change (fn [current-value]
                             (let [options {:given-name given-name
                                            :family-name family-name
                                            :current-value current-value
                                            :user-uuid (:uuid user)}]
                               (if (true? @current-value)
                                 (add-alpha-user options)
                                 (remove-alpha-user options))))
                :label "Alpha User"}]])

           (when (true? @can-manage-super-users)
             [:div {:class (:super-user-toggle-container classes)}
              [toggle/render
               {:default-value content-provider-user
                :pass-atom-on-change true
                :on-change (fn [current-value]
                             (let [options {:given-name given-name
                                            :family-name family-name
                                            :current-value current-value
                                            :user-uuid (:uuid user)}]
                               (if (true? @current-value)
                                 (add-content-provider-user options)
                                 (remove-content-provider-user options))))
                :label "Content Provider Privilege"}]])

           (when (true? @can-manage-super-users)
             [:div {:class (:super-user-toggle-container classes)}
              [toggle/render
               {:default-value super-user
                :pass-atom-on-change true
                :on-change (fn [current-value]
                             (let [options {:given-name given-name
                                            :family-name family-name
                                            :current-value current-value
                                            :user-uuid (:uuid user)}]
                               (if (true? @current-value)
                                 (add-super-user options)
                                 (remove-super-user options))))
                :label "Super User"}]])]]

         [:table {:class (cond-> [(:table-container classes)]
                           (false? @show-permissions-table)
                           (conj (:table-container-collapsed classes)))}
          (when (true? @show-permissions-table)
            [:<>
             [:thead
              [:tr {:class (:table-header-row classes)}
               [:th {:style {:min-width curriculum-column-min-width
                             :width "100%"}}
                "Curriculum Name"]
               [:th {:align :center
                     :class (:column-container classes)} "Admin"]
               [:th {:align :center
                     :class (:column-container classes)} "Content Manager"]
               [:th {:align :center
                     :class (:column-container classes)} "Insights Viewer"]
               (when (feature-enabled? :learner-table)
                 [:th {:align :center
                       :class (:column-container classes)} "Project Dashboards"])
               ;; Placeholder for async loading
               [:th {:align :center
                     :class (:async-container classes)}]]]
             [:tbody
              (for [{curriculum-uuid :uuid :as curriculum}
                    (->> curricula
                         (vals)
                         (sort-by :name))]
                ^{:key curriculum-uuid}
                [render-curriculum-permissions
                 {:curriculum curriculum
                  :curriculum-permissions (get curricula-permissions
                                               curriculum-uuid)
                  :user-uuid (:uuid user)
                  :super-user super-user}])]])]]))))

(defn- render-user-list
  [{:keys [users can-manage-super-users curricula]}]
  (let [{:keys [user-list]} users]
    [:ul {:class (:user-row-container classes)}
     (for [{:keys [uuid] :as user} user-list]
       ^{:key uuid}
       [render-user-row {:user user
                         :can-manage-super-users can-manage-super-users
                         :curricula curricula}])]))

(defn- render-no-users-message
  []
  [:div {:class (:no-users-message-container classes)}
   [text/title-40 :p
    {:class (:no-users-message classes)}
    "No users found"]])

(defn- render-filter-checkbox
  [{:keys [label filter-key search-filters search-fn]}]
  [:li
   [checkbox/render-v2
    {:label label
     :on-change #(do
                   (swap! search-filters assoc filter-key %)
                   (search-fn))
     :default-value false}]])

(defn- render-search-box
  [{:keys [is-searching
           can-manage-super-users
           search-filters
           search-value]}]
  (let [search-users-fn
        (fn []
          (search-users {:q @search-value
                         :filter @search-filters
                         :page 0}))

        search-users-filter-fn
        (fn []
          (search-users-instant {:q @search-value
                                 :filter @search-filters
                                 :page 0}))]
    [:div {:class (:user-search-container classes)}
     [:div {:style {:position :relative}}
      [text/title-30 :p "Search Users"]
      (when (true? @is-searching)
        [:div {:class (:loading-container classes)}
         [icon/icon {:class "fa-spin"} :loading]])]

     [:form {:autoComplete :off
             :on-submit #(j/call % :preventDefault)}
      [:input
       {:type :search
        :autoComplete :off
        :placeholder "Email or name"
        :value @search-value
        :style {:margin-bottom (gs [:spacing :p20])}
        :on-change (fn [e]
                     (let [search (-> e .-target .-value)]
                       (reset! search-value search)
                       (search-users-fn)))}]]

     [:div {:style {:display :flex
                    :align-items :center}}
      [text/caption-30 :p
       {:style {:margin-right (gs [:spacing :p20])}}
       "Filters: "]
      [:ul {:class (:filters-container classes)}
       (when (true? @can-manage-super-users)
         [render-filter-checkbox
          {:label "Super User"
           :filter-key :super-user
           :search-fn search-users-filter-fn
           :search-filters search-filters}])
       [render-filter-checkbox
        {:label "Admin"
         :filter-key :admin
         :search-fn search-users-filter-fn
         :search-filters search-filters}]
       [render-filter-checkbox
        {:label "Content Manager"
         :filter-key :content-manager
         :search-fn search-users-filter-fn
         :search-filters search-filters}]
       [render-filter-checkbox
        {:label "Insights Viewer"
         :filter-key :insights-viewer
         :search-fn search-users-filter-fn
         :search-filters search-filters}]
       (when (feature-enabled? :learner-table)
         [render-filter-checkbox
          {:label "Project Dashboards"
           :filter-key :project-dashboard-viewer
           :search-fn search-users-filter-fn
           :search-filters search-filters}])
       [render-filter-checkbox
        {:label "Test User"
         :filter-key :test-user
         :search-fn search-users-filter-fn
         :search-filters search-filters}]]]]))

(defn render
  [_]
  (let [users (re-frame/subscribe [:users])
        curricula (re-frame/subscribe [:curricula-map])
        is-searching (re-frame/subscribe [:request/is-dispatching
                                          :users/search-users])
        can-manage-super-users (re-frame/subscribe
                                [:authentication/has-access?
                                 :users/can-manage-super-users])
        search-value (reagent/atom "")
        search-filters (reagent/atom (cond-> {:super-user false
                                              :admin false
                                              :insights-viewer false
                                              :test-user false}
                                       (feature-enabled? :learner-table)
                                       (assoc :project-dashboard-viewer false)))]
    (fn [{:keys [resource-status]}]
      (if (ku/all-resources-loaded? resource-status)
        (let [pagination (:pagination @users)]
          [:div {:class (:page-container classes)}
           [:div {:class (:banner-container classes)}
            [text/title-40 :h3
             {:class (:title classes)}
             "User Management"]]
           [:div {:class (:search-area-container classes)}
            [text/body-20 :p
             "Search users in the sytem to view and adjust their permissions."]
            [render-search-box {:is-searching is-searching
                                :can-manage-super-users can-manage-super-users
                                :search-filters search-filters
                                :search-value search-value}]]
           (if (true? @is-searching)
             [loading/render
              {:loading-message "Please wait while we refine your search"}]
             [:div {:class (:body-container classes)}
              (if (empty? @users)
                [render-no-users-message]

                [:div {:class (:results-container classes)}
                 [pagination-controls/render-results pagination]

                 [render-user-list
                  {:users @users
                   :can-manage-super-users can-manage-super-users
                   :curricula @curricula}]])])

           [render-user-list-pagination {:pagination pagination
                                         :search-value search-value
                                         :search-filters search-filters}]])
        [loading/render
         {:loading-message "Please wait while we fetch the user records"}]))))
